import * as React from 'react';
import { Boxes, Package } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Order } from '@klareospace/pharos-lib-types';
import { DrawerSectionTitle } from '@klareospace/pharos-lib-web-utils';

import { HandlingUnitsTable } from './handling-units-table';
import { ItemsTable } from './items-table';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ContentTabProps {
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Content tab body — composition only. Renders the Handling units and Items
 * sections, each with an icon-chip title above a standalone bordered table card.
 */
export function ContentTab({ order }: ContentTabProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');

  return (
    <div className="space-y-4">
      <ContentSection icon={Boxes} title={tCommon('fields.handlingUnits')}>
        <HandlingUnitsTable handlingUnits={order.handlingUnits ?? []} />
      </ContentSection>
      <ContentSection icon={Package} title={t('drawer.content.itemsTitle')}>
        <ItemsTable items={order.items ?? []} />
      </ContentSection>
    </div>
  );
}

// ─── ContentSection ───────────────────────────────────────────────────────────

interface ContentSectionProps {
  readonly children: React.ReactNode;
  readonly icon: LucideIcon;
  readonly title: string;
}

/**
 * Shared wrapper for a content-tab section — an icon-chip title above a
 * standalone bordered table card.
 */
function ContentSection({ children, icon, title }: ContentSectionProps): React.JSX.Element {
  return (
    <div className="space-y-2">
      <DrawerSectionTitle icon={icon} title={title} />
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">{children}</div>
    </div>
  );
}
